import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { SitePage, PageHero } from '../../components/site/ui';
import { BRAND } from '../../components/site/brand';
import ContactPage from '../ContactPage';
import SEO from '../../components/SEO';

export const COMPANY = {
  name: 'VK Tours',
  legal: 'VK Tours & Travels',
  email: 'info@vktours.com',
  phone: '+91 96188 49933',
  landline: '0866 4553285',
  address: '1st Floor, Sai Plaza, Sanath Nagar, Above APCOB Bank, Kanuru, Vijayawada, Andhra Pradesh, India - 520007',
  city: 'Vijayawada',
  state: 'Andhra Pradesh',
  website: 'https://www.vktours.com',
  jurisdiction: 'Vijayawada, Andhra Pradesh',
};

export const PolicyLayout = ({ title, subtitle, lastUpdated, children, testid }) => {
  const { pathname } = useLocation();
  return (
  <SitePage>
    <SEO title={title} description={`${title} for VK Tours bookings — ${subtitle}`} path={pathname} />
    <div data-testid={testid}>
      <PageHero
        short
        crumbs={[{ label: 'Home', to: '/' }, { label: title }]}
        kicker={lastUpdated ? `Last updated: ${lastUpdated}` : undefined}
        title={title}
        lede={subtitle}
      />
      <section className="vk-section" style={{ paddingTop: 48 }}>
        <div className="vk-wrap vk-split vk-split--aside">
          <article className="vk-doc policy-content">{children}</article>
          <aside className="vk-aside">
            <div className="vk-panel vk-panel--sand">
              <h3>Questions?</h3>
              <ul className="vk-ticks">
                <li><Mail size={18} color="var(--hot)" /> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
                <li><Phone size={18} color="var(--hot)" /> <a href={BRAND.phoneLink}>{BRAND.phone}</a></li>
                <li><Phone size={18} color="var(--hot)" /> <a href={BRAND.landlineLink}>{BRAND.landline}</a></li>
                <li><MapPin size={18} color="var(--hot)" /> <span>{COMPANY.address}</span></li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  </SitePage>
  );
};

// ============================================================
//  PRIVACY POLICY
// ============================================================
export const PrivacyPolicy = () => (
  <PolicyLayout title="Privacy Policy" subtitle={`How ${COMPANY.name} collects, uses, and protects your information.`} lastUpdated="03 November 2026" testid="privacy-policy-page">
    <p>{COMPANY.legal} ("we", "us", "our") operates {COMPANY.website} and provides travel-related products and services including tour packages, flight & hotel bookings, visa assistance, travel insurance, and foreign exchange services. We are committed to protecting your personal information and your right to privacy.</p>

    <h2>1. Information We Collect</h2>
    <p>We collect the following information when you interact with us:</p>
    <ul>
      <li><b>Identity & Contact:</b> Name, email, phone number, postal address, date of birth, gender, nationality.</li>
      <li><b>Travel Documents:</b> Passport number, expiry, country of issue, visa details (only when required for the service you book).</li>
      <li><b>Payment:</b> We do <b>not</b> store full credit/debit card numbers. Payments are processed by our PCI-DSS certified payment gateway partners. We store only the transaction reference, amount, and method.</li>
      <li><b>Booking Information:</b> Travel destination, dates, traveller details, preferences, special requests.</li>
      <li><b>Communication:</b> Records of your enquiries, emails, calls, and messages exchanged with us.</li>
      <li><b>Technical Data:</b> IP address, browser, device information, pages visited, cookies (see Cookie Policy below).</li>
    </ul>

    <h2>2. How We Use Your Information</h2>
    <ul>
      <li>To process your bookings, issue invoices, and deliver the services you purchase.</li>
      <li>To verify your identity and complete KYC requirements for visa, forex, and insurance products.</li>
      <li>To communicate with you about your trip — itineraries, vouchers, updates, emergency notices.</li>
      <li>To respond to enquiries, complaints, and customer support requests.</li>
      <li>To send marketing communications about offers and packages (you can opt out anytime).</li>
      <li>To comply with legal obligations under Indian law including the Income Tax Act, GST Act, and the Information Technology Act.</li>
      <li>To improve our services, analyse trends, and prevent fraud.</li>
    </ul>

    <h2>3. Sharing Your Information</h2>
    <p>We only share your data with trusted third parties strictly to deliver the services you book:</p>
    <ul>
      <li><b>Travel suppliers:</b> Airlines, hotels, transporters, cruise companies, visa consulates — only the data required for the booking (e.g., passport for visa).</li>
      <li><b>Payment gateways:</b> Our PCI-DSS certified payment gateway partner(s) to handle transactions securely.</li>
      <li><b>Government authorities:</b> When required by law (tax, anti-money-laundering, FEMA, court orders).</li>
      <li><b>Service providers:</b> Email, SMS, WhatsApp gateways, cloud hosting (MongoDB Atlas), accounting platforms — all bound by confidentiality.</li>
      <li><b>Advertising partners:</b> Google (Google Ads) and Meta (Facebook and Instagram) may receive your phone number or email in hashed (encrypted) form so that we can show you relevant travel offers, measure our ads, or avoid showing you ads you don't need. They act as our service providers and may not use this data for any other purpose.</li>
    </ul>
    <p>We do <b>not</b> sell, rent, or trade your personal data to any third party for marketing purposes.</p>

    <h2>4. Data Security</h2>
    <ul>
      <li>All data is stored on encrypted MongoDB Atlas servers with TLS 1.2+ in transit and AES-256 encryption at rest.</li>
      <li>Customer passwords and admin/agent credentials are hashed using bcrypt (one-way) — never stored in plain text.</li>
      <li>Access to customer data is restricted to authorized staff on a need-to-know basis with audit logging.</li>
      <li>Backups are taken automatically by our cloud database provider.</li>
    </ul>

    <h2>5. Cookies & Tracking</h2>
    <p>We use cookies to keep you signed in, remember preferences, and analyze site traffic. You can disable cookies in your browser settings — some site features may not function without them. We use Google Analytics (or equivalent) to understand visitor behaviour in aggregate, and Google Ads and Meta tags to measure our ads and show relevant offers to past visitors. You can opt out of personalised ads in your Google and Meta ad settings, or ask us to stop using your details for advertising by writing to us.</p>

    <h2>6. Your Rights</h2>
    <p>Under applicable Indian data protection laws and the upcoming Digital Personal Data Protection Act, 2023, you have the right to:</p>
    <ul>
      <li>Access a copy of the personal data we hold about you.</li>
      <li>Request correction of inaccurate information.</li>
      <li>Request deletion of your data (subject to legal/regulatory retention requirements).</li>
      <li>Opt out of marketing communications by clicking "unsubscribe" or contacting us.</li>
      <li>Withdraw consent at any time.</li>
    </ul>
    <p>To exercise any of these rights, email us at <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>. We will respond within 30 days.</p>

    <h2>7. Data Retention</h2>
    <p>We retain booking, invoice, and tax records for 8 years as required by Indian Income Tax and GST law. Marketing data is retained until you opt out. Enquiry data is retained for 3 years unless you request earlier deletion.</p>

    <h2>8. Children's Privacy</h2>
    <p>Our services are intended for adults aged 18 and above. We do not knowingly collect personal data from children under 18 without parental consent. If a parent/guardian books on behalf of a minor, we will process the minor's data solely for trip-related purposes.</p>

    <h2>9. Changes to This Policy</h2>
    <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with a revised "Last updated" date. Material changes will be notified via email.</p>

    <h2>10. Grievance Officer</h2>
    <p>For any privacy concerns, complaints, or questions, please write to our Grievance Officer:</p>
    <p>
      <b>{COMPANY.legal} – Grievance Officer</b><br />
      Email: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a><br />
      Phone: {COMPANY.phone} · {COMPANY.landline}<br />
      Address: {COMPANY.address}
    </p>
  </PolicyLayout>
);

// ============================================================
//  TERMS & CONDITIONS
// ============================================================
export const TermsConditions = () => (
  <PolicyLayout title="Terms & Conditions" subtitle={`The terms governing your use of ${COMPANY.name} services.`} lastUpdated="07 February 2026" testid="terms-page">
    <p>These Terms & Conditions ("Terms") govern your use of {COMPANY.website} and the services provided by {COMPANY.legal} ("we", "us", "our"). By accessing our website or booking any service, you agree to be bound by these Terms.</p>

    <h2>1. Services Offered</h2>
    <p>We offer the following travel-related services:</p>
    <ul>
      <li>Pre-packaged and customised tour packages (domestic & international)</li>
      <li>Flight, hotel, and cruise bookings</li>
      <li>Visa assistance services</li>
      <li>Travel insurance facilitation</li>
      <li>Foreign exchange (forex) services</li>
      <li>Group tours, MICE, and pilgrimage tours</li>
    </ul>
    <p>We act as an <b>intermediary</b> between you and travel suppliers (airlines, hotels, transporters, consulates). We are <b>not</b> the operator of these underlying services unless explicitly stated.</p>

    <h2>2. Booking Process</h2>
    <ul>
      <li>All bookings are subject to availability and confirmation from the underlying supplier.</li>
      <li>Prices quoted are valid for the period stated in the quotation. They may change due to exchange-rate fluctuations, fuel surcharges, supplier rate revisions, or government taxes.</li>
      <li>A booking is confirmed only after we receive full payment or the specified advance, AND we issue a written booking confirmation/invoice.</li>
      <li>You must provide accurate information including correct names (as per passport), travel dates, and contact details. We are not liable for losses due to incorrect information you provide.</li>
    </ul>

    <h2>3. Payment Terms</h2>
    <ul>
      <li>Advance payment as per quotation (typically 25–50%) is required to confirm booking.</li>
      <li>Balance must be paid at least 21 days before departure for international tours and 15 days for domestic, unless otherwise agreed in writing.</li>
      <li>Payments can be made via UPI, debit/credit card, bank transfer, or any payment gateway listed on our site.</li>
      <li>All prices are in Indian Rupees (INR) unless stated otherwise.</li>
      <li>GST is applicable as per Indian tax law (typically 5% on tour operator services without ITC).</li>
    </ul>

    <h2>4. Passports, Visas & Travel Documents</h2>
    <p>It is the traveller's sole responsibility to ensure:</p>
    <ul>
      <li>Valid passport with at least 6 months validity beyond return date and 2 blank pages.</li>
      <li>Correct visa(s) for all destinations being visited or transited.</li>
      <li>Required vaccinations / health certificates.</li>
      <li>Compliance with destination country's entry rules and customs regulations.</li>
    </ul>
    <p>We assist with visa applications but cannot guarantee visa approval — that decision rests solely with the issuing consulate.</p>

    <h2>5. Pricing, Inclusions & Exclusions</h2>
    <p>What's included and excluded in each package is specified in the package description and final quotation. Generally <b>not included</b> unless mentioned: personal expenses, tips, optional tours, anything not explicitly listed under "Inclusions", and price increases imposed by suppliers after booking confirmation.</p>

    <h2>6. Cancellation & Refunds</h2>
    <p>Cancellation and refund terms are detailed in our separate <Link to="/cancellation-policy" className="text-emerald-700 underline">Cancellation Policy</Link>. By booking with us you agree to those terms.</p>

    <h2>7. Changes by Us</h2>
    <p>We reserve the right to modify itineraries, hotels, or transport in exceptional circumstances (operational, safety, weather, government restrictions). We will inform you promptly and offer the closest possible alternative. If the change is "significant" you may either accept or cancel and receive a refund as per our Cancellation Policy.</p>

    <h2>8. Liability</h2>
    <p>Our liability is limited to the amount paid by you for the affected service. We are <b>not liable</b> for:</p>
    <ul>
      <li>Acts of God, force majeure events, war, riots, strikes, epidemic, pandemic, natural disasters.</li>
      <li>Acts or omissions of underlying suppliers (airlines, hotels, etc.).</li>
      <li>Loss, theft, damage to baggage or personal belongings.</li>
      <li>Delays, cancellations, or schedule changes by airlines/transporters.</li>
      <li>Visa rejections.</li>
      <li>Any indirect, consequential, or punitive damages.</li>
    </ul>
    <p>We strongly recommend buying comprehensive travel insurance for every trip.</p>

    <h2>9. Behaviour of Travellers</h2>
    <p>Travellers must conduct themselves in a manner that does not endanger or inconvenience other guests, staff, or property. We reserve the right to remove from a tour any person whose behaviour is considered detrimental — without refund.</p>

    <h2>10. Intellectual Property</h2>
    <p>All content on {COMPANY.website} (text, images, logos, packages) is owned by {COMPANY.legal} or licensed to us. You may not copy, reproduce, or commercially exploit it without written permission.</p>

    <h2>11. Governing Law & Jurisdiction</h2>
    <p>These Terms are governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction of the courts at <b>{COMPANY.jurisdiction}</b>.</p>

    <h2>12. Contact</h2>
    <p>For any questions about these Terms, write to us at <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.</p>
  </PolicyLayout>
);

// ============================================================
//  CANCELLATION POLICY
// ============================================================
export const CancellationPolicy = () => (
  <PolicyLayout title="Cancellation Policy" subtitle="Standard charges for cancelling your booking with us." lastUpdated="07 February 2026" testid="cancellation-policy-page">
    <p>This Cancellation Policy applies to all bookings made with {COMPANY.legal}. By making a booking you accept the cancellation charges below. For any cancellation, please email us at <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> or call {COMPANY.phone}. Cancellation requests are processed only in writing and effective from the date we receive the email.</p>

    <h2>1. Cancellation by the Customer</h2>

    <h3>A. International Tour Packages</h3>
    <table>
      <thead><tr><th>Days before departure</th><th>Cancellation charge</th></tr></thead>
      <tbody>
        <tr><td>More than 60 days</td><td>25% of total package cost OR advance paid, whichever is higher</td></tr>
        <tr><td>45 – 60 days</td><td>50% of total package cost</td></tr>
        <tr><td>30 – 44 days</td><td>75% of total package cost</td></tr>
        <tr><td>Less than 30 days</td><td>100% of total package cost (no refund)</td></tr>
        <tr><td>No-show / after departure</td><td>100% (no refund)</td></tr>
      </tbody>
    </table>

    <h3>B. Domestic Tour Packages</h3>
    <table>
      <thead><tr><th>Days before departure</th><th>Cancellation charge</th></tr></thead>
      <tbody>
        <tr><td>More than 30 days</td><td>15% of package cost OR advance, whichever is higher</td></tr>
        <tr><td>15 – 30 days</td><td>30% of package cost</td></tr>
        <tr><td>8 – 14 days</td><td>50% of package cost</td></tr>
        <tr><td>4 – 7 days</td><td>75% of package cost</td></tr>
        <tr><td>0 – 3 days / no-show</td><td>100% (no refund)</td></tr>
      </tbody>
    </table>

    <h3>C. Air Tickets, Hotel-only, Visa, Insurance, Forex</h3>
    <p>These are subject to the cancellation rules of the respective supplier (airline, hotel, consulate, insurer). Such charges are non-negotiable and will be applied as per the supplier's policy. Our service fees, GST and any non-refundable portions are not refundable.</p>

    <h3>D. Cruise & Special Tours (group, MICE, pilgrimage)</h3>
    <p>Cruise lines and group tour operators have their own stricter cancellation policies, often non-refundable beyond 60–90 days before departure. The specific terms will be shared in your quotation/booking confirmation and override the generic schedule above.</p>

    <h2>2. Peak Season & Special Events</h2>
    <p>For travel during peak periods (Christmas/New Year, Diwali, summer holidays, long weekends, festivals like Hajj/Eid/Onam), <b>100% cancellation charges apply for all cancellations made within 45 days of departure</b>, regardless of the standard schedule above.</p>

    <h2>3. Cancellation by Us</h2>
    <p>If we have to cancel your booking (due to force majeure, insufficient group size for group tours, supplier failure, government restrictions), we will:</p>
    <ul>
      <li>Refund the total amount paid by you (less any non-refundable supplier charges already incurred), OR</li>
      <li>Offer an alternative trip of equivalent value, OR</li>
      <li>Issue a credit note valid for 12 months toward a future booking.</li>
    </ul>
    <p>We are <b>not liable</b> for any additional costs (visa fees, vaccinations, baggage, gear purchased) you may have incurred.</p>

    <h2>4. Refund Process</h2>
    <ul>
      <li>All refunds (less applicable cancellation charges) will be processed within <b>7–14 working days</b> after we receive any required cancellation confirmations from suppliers.</li>
      <li>Refunds are issued to the <b>same payment method</b> used originally. Bank transfers may take an additional 3–5 working days to reflect in your account.</li>
      <li>GST collected on cancellation charges is non-refundable per Indian tax law.</li>
      <li>For forex products, refunds are made at the prevailing exchange rate on the date of refund, not the date of purchase. The difference is borne by the customer.</li>
    </ul>

    <h2>5. Date Changes & Rescheduling</h2>
    <ul>
      <li>Date changes are treated as a fresh booking. Any difference in cost will be charged.</li>
      <li>If requested more than 45 days before departure, a service fee of ₹2,500 per person + actual supplier charges apply.</li>
      <li>Within 45 days, cancellation charges as per schedule above apply.</li>
    </ul>

    <h2>6. Refund Method</h2>
    <p>If you paid via credit/debit card or UPI, refund will be initiated to the same card/account. For payments via bank transfer or cheque, refund will be made by bank transfer to the customer's registered bank account (we may request a cancelled-cheque for verification).</p>

    <h2>7. Disputes</h2>
    <p>Any dispute over cancellation charges must be raised in writing within <b>15 days</b> of receiving our refund calculation. After this period, the cancellation is deemed accepted. All disputes are subject to the exclusive jurisdiction of the courts at <b>{COMPANY.jurisdiction}</b>.</p>

    <h2>8. Force Majeure</h2>
    <p>In the event of force majeure (war, pandemic, natural disaster, governmental travel ban, riots, strikes), we will use reasonable efforts to recover refunds from suppliers and pass them to you. We cannot guarantee refunds beyond what suppliers themselves refund to us.</p>
  </PolicyLayout>
);

// ============================================================
//  REFUND POLICY (sometimes payment gateways need this explicitly)
// ============================================================
export const RefundPolicy = () => (
  <PolicyLayout title="Refund Policy" subtitle="How refunds are processed for cancelled or modified bookings." lastUpdated="07 February 2026" testid="refund-policy-page">
    <h2>1. Eligibility for Refund</h2>
    <p>You may be eligible for a refund (in part or full) under the following circumstances:</p>
    <ul>
      <li>You cancel your booking and the applicable cancellation charges (see <Link to="/cancellation-policy" className="text-emerald-700 underline">Cancellation Policy</Link>) leave a refundable balance.</li>
      <li>We cancel your booking (force majeure, supplier failure, group size, etc.).</li>
      <li>Duplicate or erroneous payment made to us.</li>
      <li>Service was not delivered as promised AND the deficiency is attributable to us (not to the underlying supplier).</li>
    </ul>

    <h2>2. Non-Refundable Items</h2>
    <ul>
      <li>Visa application fees once submitted to the consulate.</li>
      <li>Travel insurance premiums after policy issuance.</li>
      <li>Air tickets after issuance (subject to airline rules).</li>
      <li>GST collected on any portion of the booking that becomes a cancellation charge.</li>
      <li>Our service / booking / convenience fees, once a booking is confirmed.</li>
      <li>Any third-party charges already paid out to suppliers on your behalf.</li>
    </ul>

    <h2>3. Refund Timeline</h2>
    <ul>
      <li><b>Initiation:</b> Within 7 working days of cancellation confirmation.</li>
      <li><b>Reflection in your account:</b></li>
      <ul>
        <li>UPI: 1–3 working days</li>
        <li>Credit/Debit card: 5–10 working days (depends on your bank)</li>
        <li>Net-banking / Bank transfer: 3–7 working days</li>
        <li>Cheque: 7–14 working days</li>
      </ul>
    </ul>

    <h2>4. Refund Method</h2>
    <p>All refunds are made to the <b>same payment source</b> used at the time of booking, in compliance with RBI guidelines. We do not refund to third-party accounts.</p>

    <h2>5. Disputes & Chargebacks</h2>
    <p>Before initiating a chargeback through your bank/card issuer, please contact us at <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>. We aim to resolve any dispute within 7 days. Unjustified chargebacks may result in legal action and recovery of bank charges.</p>

    <h2>6. Currency Conversion</h2>
    <p>For payments made in foreign currency, refunds will be processed in Indian Rupees at the prevailing exchange rate on the refund date. Exchange-rate gains/losses are borne by the customer.</p>
  </PolicyLayout>
);

// ============================================================
//  SHIPPING / SERVICE DELIVERY POLICY (required by most payment gateways for merchant onboarding)
// ============================================================
export const ShippingPolicy = () => (
  <PolicyLayout title="Service Delivery Policy" subtitle="How we deliver our travel services to you." lastUpdated="07 February 2026" testid="shipping-policy-page">
    <h2>1. Nature of Our Services</h2>
    <p>{COMPANY.legal} is a travel services company. We do <b>not</b> sell physical goods. We deliver <b>services</b> — bookings, vouchers, itineraries, e-tickets, visa assistance, and on-trip support.</p>

    <h2>2. Delivery of Booking Confirmations & Vouchers</h2>
    <ul>
      <li>Booking confirmations and tax invoices are sent via email and/or WhatsApp within <b>1 working day</b> of payment realisation.</li>
      <li>Hotel vouchers, e-tickets, and final itineraries are sent <b>at least 3 days before departure</b> for domestic tours and <b>7 days before departure</b> for international tours, once supplier vouchers are received.</li>
      <li>For last-minute bookings (within 7 days of departure), all documents are shared as soon as suppliers confirm — typically within 24 hours of full payment.</li>
    </ul>

    <h2>3. Visa & Document Services</h2>
    <ul>
      <li>Visa applications are submitted to the relevant consulate within the agreed timeline once all documents and fees are received from you.</li>
      <li>Visa processing time depends entirely on the consulate (typically 5–25 working days). We do not control or guarantee this timeline.</li>
      <li>Approved visas are couriered to the customer's address or available for pickup at our office (as per consulate's mode of issuance).</li>
    </ul>

    <h2>4. Forex Delivery</h2>
    <ul>
      <li>Currency notes / forex cards are delivered to the customer's registered address within <b>2–4 working days</b> of payment realisation and KYC completion. Same-day delivery may be available in select cities for a fee.</li>
      <li>Customer must produce original government-issued ID at the time of delivery.</li>
    </ul>

    <h2>5. Delivery Charges</h2>
    <p>Standard delivery is included. Express/same-day delivery, where available, will incur additional charges as quoted at the time of booking.</p>

    <h2>6. Failed / Late Delivery</h2>
    <p>If you do not receive your booking confirmation, voucher, or document within the timelines above, please immediately contact us at <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> or {COMPANY.phone}. We will investigate and resolve at the earliest.</p>

    <h2>7. Contact</h2>
    <p>For any service-delivery related queries, reach out to {COMPANY.phone} or <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.</p>
  </PolicyLayout>
);

// ============================================================
//  CONTACT US (required by most payment gateways too)
// ============================================================
export const ContactUs = () => <ContactPage />;
